import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useOsActionsOptional, useWindowById } from "@/components/ui/os-layout/OsContext";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";

export type AppLocationCodec<L> = {
  appId: string;
  fromUrl(pathname: string, search: URLSearchParams): L;
  fromParams(params: Record<string, string>): L;
  toParams(loc: L): Record<string, string>;
  toUrl(loc: L): string;
};

function paramsRecordEqual(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) {
    return false;
  }
  return aKeys.every((key) => a[key] === b[key]);
}

function splitRelativeUrl(url: string): { pathname: string; search: string } {
  const q = url.indexOf("?");
  if (q === -1) {
    return { pathname: url, search: "" };
  }
  return { pathname: url.slice(0, q), search: url.slice(q + 1) };
}

export function useAppLocation<L>(codec: AppLocationCodec<L>): [L, (patch: Partial<L>) => void] {
  const windowId = useWindowId();
  const osWindow = useWindowById(windowId);
  const osActions = useOsActionsOptional();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const inOsWindow = windowId != null && osActions != null;
  const ownParams = osWindow?.appId === codec.appId ? osWindow.params : null;

  const location = useMemo(() => {
    if (inOsWindow) {
      return codec.fromParams(ownParams ?? {});
    }
    return codec.fromUrl(pathname, searchParams);
  }, [codec, inOsWindow, ownParams, pathname, searchParams]);

  const setLocation = useCallback(
    (patch: Partial<L>) => {
      const next = { ...location, ...patch };
      if (inOsWindow) {
        if (!windowId || !ownParams) return;
        const nextParams = codec.toParams(next);
        if (!paramsRecordEqual(nextParams, ownParams)) {
          osActions?.updateWindow(windowId, { params: nextParams });
        }
        return;
      }
      const target = codec.toUrl(next);
      const { pathname: nextPath, search: nextSearch } = splitRelativeUrl(target);
      const currentSearch = searchParams.toString();
      if (pathname !== nextPath || currentSearch !== nextSearch) {
        navigate(
          { pathname: nextPath, search: nextSearch },
          { replace: true },
        );
      }
    },
    [
      codec,
      inOsWindow,
      location,
      navigate,
      osActions,
      ownParams,
      pathname,
      searchParams,
      windowId,
    ],
  );

  return [location, setLocation];
}
